import React, { useEffect, useState } from 'react'
import ModalWrapper from '../../../../../Components/ModalWrapper/ModalWrapper'
import './AddAdminModal.scss'
import {
  ADMIN_SCOPE,
  CreateAdminUserRequest,
  Platform,
  PlatformEnvironment,
  PlatformGrant,
  Role,
} from '../../../../../Models/AdminManagement'
import PlatformGrantMatrix from '../Components/PlatformGrantMatrix'
import RoleCheckList from '../Components/RoleCheckList'

interface AddAdminModalProps {
  show: boolean
  onClose: () => void
  onSubmit: (payload: CreateAdminUserRequest) => Promise<void>
  roles: Role[]
  platforms: Platform[]
  environments: PlatformEnvironment[]
}

// Mirrors CreateAdminUserRequest; only email and roleNames are required.
const AddAdminModal: React.FC<AddAdminModalProps> = ({
  show,
  onClose,
  onSubmit,
  roles,
  platforms,
  environments,
}) => {
  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [roleNames, setRoleNames] = useState<string[]>([])
  const [grants, setGrants] = useState<PlatformGrant[]>([])
  const [acknowledgedSuperAdmin, setAcknowledgedSuperAdmin] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const grantsSuperAdmin = roleNames.includes(ADMIN_SCOPE.SUPER_ADMIN)

  const reset = () => {
    setEmail('')
    setFirstName('')
    setLastName('')
    setRoleNames([])
    setGrants([])
    setAcknowledgedSuperAdmin(false)
    setError('')
  }


  useEffect(() => {
    if (!grantsSuperAdmin && acknowledgedSuperAdmin) setAcknowledgedSuperAdmin(false)
  }, [grantsSuperAdmin, acknowledgedSuperAdmin])

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email.trim()) {
      setError('Please provide an email address.')
      return
    }
    if (roleNames.length === 0) {
      setError('Please assign at least one role.')
      return
    }
    if (grantsSuperAdmin && !acknowledgedSuperAdmin) {
      setError('Please confirm you understand what super admin access grants.')
      return
    }

    const payload: CreateAdminUserRequest = {
      email: email.trim(),
      roleNames,
      ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
      ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
      ...(grants.length ? { platforms: grants } : {}),
    }

    try {
      setLoading(true)
      setError('')
      await onSubmit(payload)
      reset()
      onClose()
    } catch (err) {
      setError((err as Error)?.message || 'Failed to create administrator.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalWrapper showModal={show} closeModal={handleClose} title='Add New Administrator' size='lg'>
      <div className='AddAdminModal'>
        <form onSubmit={handleSubmit}>
          {error && <div className='alert alert-danger py-2 mb-3'>{error}</div>}

          <div className='form-group mb-3'>
            <label>Email Address *</label>
            <input
              type='email'
              className='form-control'
              placeholder='e.g. john@tradegrid.co'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className='row'>
            <div className='col-md-6 form-group mb-3'>
              <label>First Name</label>
              <input
                type='text'
                className='form-control'
                placeholder='e.g. John'
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className='col-md-6 form-group mb-3'>
              <label>Last Name</label>
              <input
                type='text'
                className='form-control'
                placeholder='e.g. Doe'
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className='form-group mb-3'>
            <label>Roles *</label>
            <RoleCheckList
              roles={roles}
              value={roleNames}
              onChange={setRoleNames}
              idPrefix='add-role'
            />
          </div>

          {grantsSuperAdmin && (
            <div className='alert alert-warning py-2 mb-3'>
              <div className='form-check mb-0'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='ack-super-admin'
                  checked={acknowledgedSuperAdmin}
                  onChange={(e) => setAcknowledgedSuperAdmin(e.target.checked)}
                />
                <label className='form-check-label' htmlFor='ack-super-admin'>
                  <strong>Super admin</strong> can create, disable and re-permission every other
                  administrator, including you. I understand.
                </label>
              </div>
            </div>
          )}

          <div className='form-group mb-4'>
            <label>Platform Access</label>
            <PlatformGrantMatrix
              platforms={platforms}
              environments={environments}
              value={grants}
              onChange={setGrants}
            />
            {grants.length === 0 && (
              <div className='text-muted small mt-2'>
                No platform access selected. They will be able to sign in but reach nothing until
                access is granted.
              </div>
            )}
          </div>

          <div className='form-group mb-2'>
            <button type='submit' className='btn btn-default w-100' disabled={loading}>
              Add Administrator{' '}
              {loading && <span className='spinner-border spinner-border-sm ms-2' />}
            </button>
          </div>
        </form>
      </div>
    </ModalWrapper>
  )
}

export default AddAdminModal
